/**
 * Every shape the console reads off the wire, in one file.
 *
 * The server routes under `/v1/org` do not exist yet. These types are derived
 * from `namu-server/docs/PLATFORM-MODEL.md`, which is the document the server
 * will be checked against, so they are a reading of the design rather than a
 * guess. They are kept together deliberately: when the routes land, reconciling
 * the console against them is one file to read side by side with the schema,
 * not thirteen. The endpoint modules next door hold only their own request
 * shapes, because a filter that exists on one list and nowhere else is not
 * shared vocabulary.
 *
 * Two conventions run through all of it.
 *
 * Instants stay strings. Nothing here revives an ISO-8601 field into a `Date`
 * on the way in, because the string carries the offset the server sent and a
 * `Date` does not: it becomes a point in time that the browser then renders in
 * whatever zone the machine is set to. A worker in Kano reading a case stamped
 * at 00:30 UTC would see yesterday's date on today's case, and nothing about
 * the screen would look wrong. Parse at the point of formatting, once, where
 * the timezone is a decision somebody made on purpose.
 *
 * Locales stay open strings rather than the mobile app's two-language union. A
 * project can run in a language this console has no interface translation for,
 * and a union would make that project's knowledge documents fail to typecheck
 * for no reason a user would recognise.
 */

/** An ISO-8601 instant, as sent. Never parsed on arrival. See above. */
export type Instant = string

/** An ISO-8601 calendar date, `YYYY-MM-DD`, with no time and no zone. */
export type CalendarDate = string

/** A BCP-47 tag, e.g. `ha` or `en-NG`. Open on purpose. */
export type Locale = string

/* ===================== Organization and staff ===================== */

/**
 * The six roles, in the order the spec ranks them.
 *
 * The order is documentation, not a permission mechanism. What each role may
 * do is decided by the explicit table in `store/permissions.ts`, and the
 * reasons that has to be explicit rather than a rank comparison are written
 * there.
 */
export const ORG_ROLES = ['owner', 'admin', 'manager', 'analyst', 'field_agent', 'viewer'] as const

export type OrgRole = (typeof ORG_ROLES)[number]

export function isOrgRole(value: unknown): value is OrgRole {
  return typeof value === 'string' && (ORG_ROLES as readonly string[]).includes(value)
}

/**
 * An organization as the server actually sends one.
 *
 * `locales` used to be declared here as a required array and no response has
 * ever carried it. The settings screen read `organization.locales.length` and
 * died on `undefined`, with a 200 in the network tab, which is the hardest
 * version of this bug to find: a missing route announces itself and a missing
 * field does not.
 *
 * It is gone rather than made optional, because the fact underneath it is that
 * an organization does not have a set of languages. A **project** does, and it
 * carries both `locales` and `defaultLocale`, because languages are a fact
 * about a community: one organization can be running maternal health in Hausa
 * in Kano and cash transfer in Kanuri in Borno. What an organization has is
 * `defaultLocale`, which is only what a project falls back to when it does not
 * name its own.
 *
 * `status` and `updatedAt` are on the wire and deliberately not here. Nothing
 * in the console reads either, and an organization that could suspend itself
 * from its own settings screen could not un-suspend itself: a suspended
 * organization fails authentication and nobody inside it can sign in to undo it.
 */
export interface Organization {
  id: string
  name: string
  /** Short, URL-safe, and shown in invitations so a person knows who invited them. */
  slug: string
  /** What a project falls back to when it does not name its own language. */
  defaultLocale: Locale
  createdAt: Instant
}

export type StaffStatus = 'invited' | 'active' | 'suspended'

export interface StaffUser {
  id: string
  email: string
  name: string
  role: OrgRole
  status: StaffStatus
  lastSeenAt: Instant | null
  createdAt: Instant
}

/* ===================== Projects ===================== */

export type ProjectStatus = 'draft' | 'active' | 'paused' | 'closed'

/**
 * A project as `/v1/org/projects` actually sends it.
 *
 * Three fields this used to declare are gone, and what they cost is worth
 * recording because the console shipped with all three. `summary` is the
 * server's `objective`, and the mismatch was invisible rather than loud: the
 * validator drops keys it does not recognise, so the sentence somebody typed
 * into the new-project dialog was accepted with a 201 and stored nowhere.
 * `startsOn` and `endsOn` have no counterpart at all, the schema having no start
 * or end date on a project, so two date pickers collected values that went
 * into the same hole. `createdBy` is on the row and deliberately not on the
 * response.
 *
 * `mustNotSay` being a plain array of phrases here rather than a list of rows
 * with reasons attached is the same story from the other end, and is discussed
 * where the settings screen edits it.
 */
export interface Project {
  id: string
  name: string
  /** What the project is trying to change. One or two sentences. */
  objective: string | null
  status: ProjectStatus
  /** What a session opens in when it does not say. Always one of `locales`. */
  defaultLocale: Locale
  locales: Locale[]
  /** What Kura refuses to say here, whatever this project's knowledge contains. */
  mustNotSay: string[]
  /** Members who have not left. Computed by the server; never sent back. */
  memberCount: number
  createdAt: Instant
  updatedAt: Instant
}

/* ===================== Knowledge ===================== */

export type KnowledgeStatus = 'draft' | 'in_review' | 'published' | 'archived'

export interface KnowledgeDocument {
  id: string
  projectId: string
  title: string
  locale: Locale
  status: KnowledgeStatus
  /** The version Kura is currently given. Null until something is published. */
  currentVersionId: string | null
  /**
   * When the guidance starts and stops being true. The end date is the field
   * that stops a passed distribution date being read out for another month,
   * so it is modelled as a first-class column rather than left to somebody
   * remembering to archive the document.
   */
  effectiveFrom: Instant | null
  effectiveUntil: Instant | null
  /**
   * Whether Kura would actually say this at the moment of the request.
   *
   * Published and live are not the same thing, and the distance between them
   * is the most confusing part of this model for the person reading the
   * screen. A document published last month with an end date of last Friday is
   * published and silent, and without this field the table shows a green
   * "Published" badge for guidance nobody has heard in a week.
   */
  liveNow: boolean
  createdAt: Instant
  updatedAt: Instant
}

/**
 * One frozen state of a document, with the text in it.
 *
 * `createdBy` and not `publishedBy`, because the server records who wrote a
 * version and nothing records who moved the pointer. Crediting the author for
 * the publication was the console's own invention and it would put the wrong
 * name beside a version that one person drafted and another signed off.
 */
export interface KnowledgeVersion {
  id: string
  documentId: string
  /** Monotonic within the document, starting at 1. */
  version: number
  title: string
  body: string
  /** Why this version exists. Written when it is saved, not when it goes live. */
  changeNote: string | null
  createdBy: string | null
  createdAt: Instant
  /** Null until this exact version has been published at least once. */
  publishedAt: Instant | null
}

/**
 * A version as the history list returns it, which is without its body.
 *
 * Fifty versions of a guidance document is several megabytes of text to render
 * fifty lines of "edited by Amina", so the server leaves the bodies out and
 * this type says so rather than letting a screen read a field that will be
 * undefined at runtime and typed as a string.
 */
export interface KnowledgeVersionSummary extends Omit<KnowledgeVersion, 'body'> {
  /** True for the version the document currently points at. */
  isCurrent: boolean
}

/* ===================== Enrolment and membership ===================== */

export interface EnrolmentCode {
  id: string
  projectId: string
  /** Short and sayable: `KANO-42`. It is spoken to Kura, not typed into a form. */
  code: string
  /**
   * What cohort this was minted for. Nullable, because the server does not
   * insist on one, and a screen that assumed a string printed the word
   * `undefined` in the cohort column for any code minted through the API.
   */
  label: string | null
  maxUses: number | null
  usedCount: number
  expiresAt: Instant | null
  active: boolean
  createdAt: Instant
  updatedAt: Instant
}

export type MemberSource = 'code' | 'agent' | 'staff'

/**
 * A membership, and deliberately nothing about the person.
 *
 * `displayName` and `sharesTranscripts` were declared here and are not sent,
 * and taking them out is the point rather than a tidy-up. A roster is a list of
 * links: a name belongs to the account rather than to the organization, and
 * reading one is governed by a consent this endpoint does not check and should
 * not have to. The consequence is real and is accepted, in that the members
 * screen identifies people by an opaque account id, and the alternative was worse
 * than an ugly column, because `sharesTranscripts` arriving as `undefined` read
 * as `false` and the screen stated, of every member, that they had withheld a
 * consent nobody had asked them about.
 */
export interface ProjectMember {
  id: string
  accountId: string
  joinedAt: Instant
  source: MemberSource
  /** The member of staff who enrolled them, when a human did. */
  enrolledBy: string | null
  /** Which code was redeemed, so where a member came from stays answerable. */
  codeId: string | null
  /** Set rather than deleted, so last quarter's numbers do not change. */
  leftAt: Instant | null
}

/* ===================== Conversations ===================== */

/**
 * One conversation, with no word of it.
 *
 * `topics` used to be declared here and is gone, and its absence is the point
 * rather than a tidy-up. Nothing in the platform labels a single conversation
 * with a topic: `topic_signals` counts a topic against a project and a period,
 * which is an aggregate over many people and cannot be attributed back to one
 * call. The column existed, was always empty, and read as data that had failed
 * to load. Per-conversation topics need a classifier that does not exist; the
 * ranked version of the question lives on the Insights screens.
 */
export interface ConversationSummary {
  id: string
  projectId: string
  projectName: string
  /** Null when the caller may not be told which member this was. */
  accountId: string | null
  startedAt: Instant
  durationSeconds: number
  locale: Locale
  turnCount: number
  /** True when a rule fired during this conversation and raised it for a human. */
  escalated: boolean
  /**
   * Whether this organization may currently read this member's words.
   *
   * A hint, not a permission: it was true of the instant the page was built,
   * and the read itself asks again. False means the summary is all there will
   * be for this row until that member decides otherwise.
   */
  sharesTranscripts: boolean
}

export interface ConversationTurn {
  /** Stable within one transcript. Turns carry no id of their own on the wire. */
  key: string
  /** One number covers a question and its answer, so it repeats across a pair. */
  ordinal: number
  speaker: 'member' | 'kura'
  /** Null when this turn was recorded without permission to keep the words. */
  text: string | null
  startedAt: Instant
  /** True when the speaker was cut off, which is a fact about the assistant. */
  interrupted: boolean
}

/**
 * Which approved text Kura had in front of it during one conversation.
 *
 * The honest answer to "why did it say that": the versions actually composed
 * into the prompt, recorded at the time. Per conversation and not per answer,
 * because the briefing composes the whole prompt when the session opens, so a
 * citation beside each reply would be blank beside almost every one.
 */
export interface KnowledgeCitation {
  versionId: string
  documentId: string
  title: string
  version: number
}

/** Why a transcript is not being shown. */
export type TranscriptRefusal =
  | 'store_transcripts_not_granted'
  | 'share_with_org_not_granted'
  | 'nothing_was_recorded'

/**
 * A single conversation, words included when they may be released.
 *
 * Fetching one is an individual record read: it requires both
 * `store_transcripts` and `share_with_org` from that member, and it writes an
 * audit event. Both happen on the server.
 *
 * A refusal is a field rather than an error, and it carries the server's own
 * sentence. An empty transcript reads as a fault, people reload, try another
 * row, and eventually report that transcripts are broken; they are not, and
 * somebody declined. `withheld` is what lets the panel say which.
 */
export interface ConversationDetail {
  conversation: ConversationSummary
  /** Null when nothing was released. Empty when it was and there was nothing in it. */
  transcript: ConversationTurn[] | null
  /** Released under the same rule as the transcript, so empty when it was not. */
  knowledge: KnowledgeCitation[]
  withheld: { reason: TranscriptRefusal; message: string } | null
}

/* ===================== Insight ===================== */

/** How far a raised thing has got back to the person who raised it. */
export type LoopState = 'open' | 'acknowledged' | 'answered' | 'closed'

export type GapStatus = 'open' | 'answered' | 'dismissed'

/** One community's share of a question: where it was asked and how loudly. */
export interface GapCommunity {
  /** The row the three gap actions act on. A cluster has no id of its own. */
  gapId: string
  projectId: string
  /** Read through a join on the server, because an id is not a place anybody knows. */
  projectName: string
  locales: Locale[]
  askCount: number
  memberCount: number
  status: GapStatus
  loopState: LoopState | null
  resolvedByDocumentId: string | null
}

/**
 * A question, as it stands across every community that asked it.
 *
 * A cluster rather than a row, which is what the server ranks and is the whole
 * value of the list: the same question asked by nine people in Kano and seven
 * in Sokoto is sixteen people in two places, not two entries that both rank
 * below an unrelated twelve. `communities` is where the rows went, and it is
 * what the actions act on, because a document belongs to one project.
 */
export interface KnowledgeGap {
  /**
   * The gap row in the community most people asked from.
   *
   * A cluster has no id, so this is the representative row's, and it is what
   * the screens address when they carry a question to the knowledge editor. On
   * a list filtered to one project, which is how the editor looks a gap up,
   * there is exactly one community and this is it.
   */
  id: string
  projectId: string
  /** The question's identity across projects, which is what the server groups on. */
  clusterKey: string
  /**
   * What the question is about, or a sentence saying it is being withheld.
   *
   * Never a quotation. The server withholds the wording entirely until enough
   * separate people are behind the same question, because a cluster of one is
   * one person's own sentence with the function words taken out, and beside a
   * timestamp on the conversation list it becomes attributable. `labelWithheld`
   * says which of the two this is; the count, the languages, the dates and the
   * rank are unaffected either way.
   */
  question: string
  labelWithheld: boolean
  locales: Locale[]
  /** Times it was asked. The ranking is on people, which is `memberCount`. */
  askCount: number
  /** Distinct people. Forty questions from one person is a person, not a gap. */
  memberCount: number
  /**
   * The day the first person asked, `YYYY-MM-DD`, and deliberately not an
   * instant: a gap's timestamp beside a conversation's `startedAt` is what
   * turns an aggregate into an attributed sentence, so the server throws the
   * time away before sending it.
   */
  firstSeenAt: CalendarDate
  lastSeenAt: CalendarDate
  status: GapStatus
  /**
   * Whether anything has gone back to the people who asked.
   *
   * Null means no loop was ever opened for this question, which is a different
   * fact from an open one. A gap can be answered, meaning somebody wrote the
   * document, while its loop is open, meaning nobody has been told.
   */
  loopState: LoopState | null
  /** Set once somebody writes the knowledge that closes the gap. */
  resolvedByDocumentId: string | null
  communities: GapCommunity[]
}

export type TopicDirection = 'up' | 'down' | 'flat' | 'new'

export interface TopicSignal {
  id: string
  topic: string
  projectId: string
  periodStart: Instant
  periodEnd: Instant
  askCount: number
  memberCount: number
  /** What the period before counted. Null when there was no such period. */
  previousCount: number | null
  /** Asks gained or lost against that period. */
  change: number
  /**
   * How many times over, to two places, or null when there is nothing to
   * divide by. A topic nobody had heard before has no ratio, and reporting one
   * would put "up 100%" beside two people and beside four hundred.
   */
  changeRatio: number | null
  direction: TopicDirection
}

export type RumorVerdict = 'unreviewed' | 'true' | 'false' | 'misleading'

export interface RumorSignal {
  id: string
  projectId: string
  /**
   * What people are saying, in the words they are saying it in, or a sentence
   * saying it is being withheld.
   *
   * Verbatim by design: how a rumour is phrased is what makes it spread, and a
   * staff paraphrase is a different claim nobody is repeating. That makes this
   * the field nearest to somebody's own words anywhere in Insight, so the
   * server holds it back until more than a couple of people are behind it.
   */
  claim: string
  claimWithheld: boolean
  locale: Locale
  /** Times it has been heard, which is what velocity is computed from. */
  heardCount: number
  /** Distinct people. A claim one person is repeating is not a rumour. */
  memberCount: number
  /** Days, not instants, for the same reason a gap's dates are. */
  firstHeardAt: CalendarDate
  lastHeardAt: CalendarDate
  verdict: RumorVerdict
  /**
   * The day a person decided, so a verdict that has aged can be seen to have.
   *
   * Who decided is deliberately not here. Nothing renders it, a viewer may read
   * this list and may not read the staff list, so the id would resolve to
   * nothing for the people most likely to see it, and the audit log already
   * records the name.
   */
  reviewedAt: CalendarDate | null
  /** The correction, once somebody has written one. */
  knowledgeDocumentId: string | null
}

/* ===================== Cases ===================== */

export type CaseStatus =
  | 'new'
  | 'assessment'
  | 'planned'
  | 'in_progress'
  | 'closed'
  | 'reopened'

export type CasePriority = 'low' | 'normal' | 'high' | 'urgent'

export interface Case {
  id: string
  projectId: string
  /** Null for a case opened about somebody who never used the app. */
  accountId: string | null
  /** Short and human: what a caseworker says out loud on the phone. */
  reference: string
  category: string
  priority: CasePriority
  status: CaseStatus
  assignedTo: string | null
  openedAt: Instant
  closedAt: Instant | null
  closeReason: string | null
  /*
   * There is deliberately no `loopState` here.
   *
   * A feedback loop is its own row keyed on what raised it, `(source_type,
   * source_id)`, and a case is one of the five things that can raise one. It is
   * not a column on the case and `GET /v1/org/cases` does not return it, so the
   * field this interface used to declare was read off every response as
   * `undefined` and drawn as an empty badge on every row of the caseload. It
   * belongs on whatever reads `feedback_loops`, and it comes back here when the
   * server joins it in rather than when the console asserts it does.
   */
}

export type CaseEventKind =
  | 'note'
  | 'status_change'
  | 'assignment'
  | 'referral'
  | 'contact'
  | 'consent'
  | 'escalation'

/**
 * One entry on a case's timeline.
 *
 * Cases are never edited in place, so this list is the record rather than a
 * history of one. `metadata` carries whatever the kind needs; it is read by
 * the renderer for that kind and by nothing else.
 */
export interface CaseEvent {
  id: string
  caseId: string
  kind: CaseEventKind
  at: Instant
  actorUserId: string | null
  /** Who it was, in words. Kept beside the id because the id is nulled on deletion. */
  actorLabel: string | null
  body: string | null
  /**
   * Where a `status_change` came from and went to, as columns rather than as
   * metadata.
   *
   * They are separate fields because that is where the server puts them, and
   * the console reading `metadata.to` instead is why every transition on every
   * timeline said "the status of this case changed" and none of them said what
   * it changed to. `null` on every other kind of entry.
   */
  fromStatus: CaseStatus | null
  toStatus: CaseStatus | null
  /** The conversation that raised this, when one did. */
  sessionId: string | null
  metadata: Record<string, unknown> | null
}

/**
 * What a case consent was given for, from a fixed list rather than free text.
 *
 * A closed list because the referral rule is enforced on it: the database key
 * behind a referral is `(consent_id, case_id, purpose, granted)`, so only a
 * consent recorded for `referral` can carry one. When this was a free-text
 * field the console let somebody type "Referral to a shelter" and the server
 * refused the referral afterwards for a reason the form had given no hint of.
 *
 * `information_sharing` is deliberately not close enough. Agreeing that a
 * summary may be shared is not agreeing to be sent somewhere.
 */
export type CaseConsentPurpose =
  | 'referral'
  | 'service_provision'
  | 'information_sharing'
  | 'contact'

/** How far a consent reaches. Also a closed list, and also enforced. */
export type CaseConsentScope = 'summary' | 'contact_details' | 'full_record'

export interface CaseConsent {
  id: string
  caseId: string
  purpose: CaseConsentPurpose
  grantor: 'self' | 'guardian'
  /** Who the guardian is, when one gave it. The server requires it in that case. */
  grantorNote: string | null
  scope: CaseConsentScope
  /**
   * Whether they agreed.
   *
   * False is a recorded refusal and not an absence, which is the reason the
   * field exists at all: "they said no" and "nobody asked" are different facts
   * about a person, and a row that only ever meant yes could not hold the first
   * one. A refusal never covers a referral, however recent it is.
   */
  granted: boolean
  grantedAt: Instant
  expiresAt: Instant | null
  withdrawnAt: Instant | null
}

export type ReferralStatus = 'proposed' | 'sent' | 'accepted' | 'declined' | 'completed'

export interface Referral {
  id: string
  caseId: string
  toOrganization: string
  service: string
  status: ReferralStatus
  /**
   * Never null. A referral exists only against a recorded consent, by foreign
   * key rather than by a checkbox, because a boolean keeps the answer and
   * loses the question, the date and who asked.
   */
  consentId: string
  sentAt: Instant | null
}

/* ===================== Broadcasts ===================== */

export type BroadcastStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled'

export interface Broadcast {
  id: string
  projectId: string
  title: string
  /** What Kura actually says. */
  body: string
  locale: Locale
  /** A saved filter, resolved to recipients when it sends. */
  audience: Record<string, unknown> | null
  status: BroadcastStatus
  /**
   * On every broadcast the server returns, including a draft, where every
   * figure is nought. Read it through `deliveryOf` all the same: a type is a
   * promise about a server this build was compiled against, not about the one
   * answering, and this field is the one a whole screen is drawn from.
   */
  delivery: BroadcastDelivery
  scheduledFor: Instant | null
  sentAt: Instant | null
  /** "The meeting is tomorrow" has to stop being said the day after. */
  expiresAt: Instant | null
  wantsResponse: boolean
  responsePrompt: string | null
  /** Null once that member of staff has been deleted. */
  createdBy: string | null
  createdAt: Instant
  updatedAt: Instant
}

export type RecipientState =
  | 'pending'
  | 'delivered'
  | 'played'
  | 'responded'
  | 'failed'
  | 'expired'

/**
 * A count, its denominator and the ratio, together.
 *
 * The three arrive as one object from the server and are kept as one here. A
 * count handed over on its own is a count that gets put on a slide with no
 * denominator anywhere near it, which is the entire failure mode of every
 * message-delivery dashboard this product was built in reaction to.
 */
export interface DeliveryMeasure {
  count: number
  of: number
  /** Already rounded by the server, to four places. */
  rate: number
}

/**
 * The delivery counts, with `played` first because that is the headline.
 *
 * The field order here is not cosmetic. MomConnect registered over a million
 * women and fewer than 6% were still receiving a message at 6 to 12 months;
 * Kilkari reached 95% of its group, 65% listened to half, and the trial found
 * no effect on the behaviour it targeted. `sent` is the denominator and
 * nothing else, and a console that leads with it reports a number that has
 * repeatedly failed to mean anything.
 *
 * `played` and `responded` are cumulative: they are counted from the
 * timestamps, so somebody who answered is in both. `states` is the exclusive
 * breakdown of where every recipient row is sitting right now, and its `played`
 * is therefore smaller than the one above by exactly the number of people who
 * went on to reply. The two are not interchangeable and the difference is
 * everybody the broadcast worked best on.
 */
export interface BroadcastDelivery {
  played: DeliveryMeasure
  responded: DeliveryMeasure
  sent: number
  states: Record<RecipientState, number>
}

export interface BroadcastRecipient {
  id: string
  broadcastId: string
  accountId: string
  /** Only ever what the person told Kura. Null is the common case. */
  displayName: string | null
  state: RecipientState
  deliveredAt: Instant | null
  playedAt: Instant | null
  respondedAt: Instant | null
  responseText: string | null
  /**
   * Whether their words could be kept, which is not the same question as
   * whether there are any.
   *
   * False alongside a `respondedAt` is somebody who answered and has not agreed
   * to their words being stored. Rendering that as an empty reply would report
   * a person who declined as a person who said nothing.
   */
  responseRecorded: boolean
  /** Null until a reply raises a loop, which is most rows on most broadcasts. */
  loopState: LoopState | null
}

/* ===================== Field operations ===================== */

export type TaskStatus =
  | 'open'
  | 'accepted'
  | 'in_progress'
  | 'submitted'
  | 'approved'
  | 'rejected'
  | 'cancelled'

export interface FieldTask {
  id: string
  projectId: string
  title: string
  instructions: string | null
  /**
   * The staff user carrying this, and the server's own spelling for it.
   *
   * A case says `assignedTo` and a task says `assigneeId`, which is the
   * platform's inconsistency rather than this file's, and it is mirrored here
   * rather than smoothed over. Renaming a field at the boundary means the
   * console's name and the wire's name differ, and the next person comparing
   * the two halves of a divergence has to know which of the two they are
   * reading. That is how the console spent a release calling `assignedTo` on a
   * payload which has never carried one.
   */
  assigneeId: string | null
  dueAt: Instant | null
  status: TaskStatus
  priority: CasePriority
  /**
   * Where the work is, as a place name. Not coordinates and not a track: a
   * report can carry where it was filed from because that is a fact about the
   * work, and a worker's position through the day is not.
   */
  locationName: string | null
  caseId: string | null
  createdAt: Instant
}

export interface TaskReport {
  id: string
  taskId: string
  /**
   * Null once the staff account that filed it has been removed.
   *
   * The column is `on delete set null`, so this is what a report from somebody
   * who has since left the organization actually looks like. Typed as a plain
   * string, the record screen read it straight into a lookup and a departed
   * colleague's report rendered with a blank author and no explanation.
   */
  submittedBy: string | null
  /** Usually dictated through the app. The person doing the work is not a typist. */
  narrative: string
  outcome: string | null
  submittedAt: Instant
  reviewedBy: string | null
  reviewedAt: Instant | null
  reviewNote: string | null
}

/**
 * Whether the people the work was for say it happened.
 *
 * Who gets asked is chosen server-side and is never returned to the assignee,
 * which is the whole mechanism: an agent who knows which members will be asked
 * can arrange the answer.
 */
export interface TaskVerification {
  id: string
  taskId: string
  askedAt: Instant
  answeredAt: Instant | null
  confirmed: boolean | null
  note: string | null
}

/* ===================== Measurement ===================== */

export type IndicatorLevel = 'output' | 'outcome' | 'impact'
export type IndicatorMeasure = 'unit' | 'percentage' | 'qualitative'
export type IndicatorDirection = 'higher_is_better' | 'lower_is_better'

/** Shaped after the IATI 2.03 `result` element, so the export is a serializer. */
export interface Indicator {
  id: string
  projectId: string
  code: string
  title: string
  definition: string | null
  level: IndicatorLevel
  measure: IndicatorMeasure
  direction: IndicatorDirection
  unit: string | null
  baselineValue: number | null
  baselineDate: CalendarDate | null
  dataSource: string | null
  meansOfVerification: string | null
  frequency: string | null
}

export interface IndicatorPeriod {
  id: string
  indicatorId: string
  periodStart: CalendarDate
  periodEnd: CalendarDate
  targetValue: number | null
  actualValue: number | null
  status: 'planned' | 'reported' | 'verified'
  note: string | null
  dimensions: IndicatorDimension[]
}

/**
 * Disaggregation as a row, mirroring IATI's `<dimension name value/>`.
 *
 * A column per dimension is a migration every time one project wants to split
 * by disability status and another does not, and it makes the export a
 * transformation instead of a field mapping.
 */
export interface IndicatorDimension {
  name: string
  value: string
  amount: number
}

/* ===================== Audit ===================== */

export interface AuditEvent {
  id: string
  actorUserId: string | null
  actorName: string | null
  action: string
  subjectType: string
  subjectId: string | null
  at: Instant
  /** Ties a console action to the server log line it produced. */
  requestId: string | null
  metadata: Record<string, unknown> | null
}
